/**
 * Streamoznamy — co se má ukázat, když se backend uprostřed tahu rozhodne sám.
 *
 * SSE posílá `profile` (každé kolo) a `info` (compaction, denní limit switch,
 * Harmony skryté tooly, Gemini krácení paralelních callů). Původní UI oba dva
 * typy spolklo jako no-op (`CodableChatPane` SSE smyčka) — uživatel tak
 * NEVIDĚL, že server přepnul profil (např. gb10-qwen3 → gemini-flash po
 * chybě). Prompt dál mluvil dialektem původního modelu, odpověď generoval
 * jiný → navenek „model se zasekl / blábolí", i když engine běžel v pořádku.
 *
 * Tihle dva helpři rozhodují CO ukázat a JEDNOU (bez spamu při každém kole).
 */

export interface StreamNotice {
  kind: "info" | "profile-switch";
  text: string;
  /** Při profile-switch: id nového profilu, aby UI srovnalo dial. */
  profileId?: string;
  profileLabel?: string;
}

/**
 * Odstupňovaný `info` event. Vrací notice jen když nese sdělení; prázdný
 * text zahodíme (ať neletí prázdný toast).
 */
export function describeInfoNotice(evt: {
  type?: string;
  message?: string;
}): StreamNotice | null {
  if (evt?.type !== "info") return null;
  const message = (evt.message ?? "").trim();
  if (!message) return null;
  return { kind: "info", text: message };
}

/**
 * Rozhodne, jestli `profile` event znamená SKUTEČNÉ přepnutí oproti tomu, co
 * UI čekalo. Backend posílá profil každé kolo — totéž id NENÍ přepnutí.
 *
 * @param evt        { type:'profile', id, label }
 * @param expectedId id, který UI poslalo / naposledy vidělo (null = neznámý
 *                   výběr → první profile event považujeme za očekávaný,
 *                   nezačínáme toastem hned na startu)
 * @param shownLabels labely už oznámené v tomto tahu (neopakovat)
 */
export function describeProfileSwitch(
  evt: { type?: string; id?: string; label?: string },
  expectedId: string | null,
  shownLabels: ReadonlySet<string>,
): StreamNotice | null {
  if (evt?.type !== "profile" || !evt.id) return null;
  const label = evt.label ?? evt.id;
  // Start tahu: UI ještě neví jaký profil engine reálně vzal (request mohl
  // profile_id nechat na serveru). První pozorování jen nastavíme expectation,
  // netoastujeme.
  if (expectedId === null) return null;
  if (evt.id === expectedId) return null;
  if (shownLabels.has(label)) return null;
  return {
    kind: "profile-switch",
    text: `Profil přepnut na „${label}"`,
    profileId: evt.id,
    profileLabel: label,
  };
}
